import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { RevealText } from "./RevealText";
import { trackDeckDownload, trackRFQClick } from "@/lib/analytics";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="bg-nx-navy text-nx-bg mt-32 relative overflow-hidden"
    >
      <div className="px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-copper mb-6">
            Difficult to source? We find the way.
          </p>
          <RevealText
            as="p"
            className="font-cabinet font-black text-nx-bg text-3xl md:text-4xl leading-[1.05] max-w-lg tracking-tight"
            text="A global sourcing partner for industrial companies with difficult, technical or urgent requirements."
          />
          <Link
            to="/sourcing-request"
            data-testid="footer-rfq-cta"
            onClick={() => trackRFQClick("footer")}
            className="inline-flex items-center gap-2 mt-10 nx-btn on-dark"
          >
            Submit a Requirement <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-nx-steel mb-6">
            → Navigate
          </p>
          <ul className="space-y-3">
            {[
              ["/", "Home"],
              ["/capabilities", "Capabilities"],
              ["/what-we-source", "What We Source"],
              ["/industries", "Industries"],
              ["/certifications", "Certifications"],
              ["/partners", "Partner Network"],
              ["/corporate", "CSR"],
              ["/about", "About"],
              ["/sourcing-request", "Submit a Requirement"],
              ["/contact", "Contact"],
            ].map(([to, label]) => (
              <li key={to}>
                <Link
                  to={to}
                  data-testid={`footer-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-nx-bg hover:text-nx-copper transition-colors duration-300 text-sm"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4 space-y-5">
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-nx-steel mb-6">
            → Contact
          </p>
          <div className="flex items-start gap-3 text-sm">
            <MapPin size={14} className="mt-1 text-nx-copper shrink-0" />
            <div className="text-nx-bg/85">
              NEXORIQ FZ LLC · VUNE2559 Compass Building<br />Al Hulaila Industrial Zone-FZ · P.O. Box 10055<br />Ras Al Khaimah · United Arab Emirates
            </div>
          </div>
          <a
            href="tel:+971508003202"
            data-testid="footer-phone"
            className="flex items-center gap-3 text-sm text-nx-bg hover:text-nx-copper transition-colors"
          >
            <Phone size={14} className="text-nx-copper" /> +971 50 800 3202
          </a>
          <a
            href="mailto:source@nexoriq.co"
            data-testid="footer-email"
            className="flex items-center gap-3 text-sm text-nx-bg hover:text-nx-copper transition-colors"
          >
            <Mail size={14} className="text-nx-copper" /> source@nexoriq.co
          </a>
        </div>
      </div>

      {/* Massive brand text */}
      <div className="border-t border-nx-steel/30 overflow-hidden">
        <div className="px-6 md:px-10 py-10 flex items-end justify-between gap-4">
          <div className="font-cabinet font-black tracking-tighter text-nx-bg leading-none text-[16vw] md:text-[15vw]" style={{ letterSpacing: "-0.05em" }}>
            NEXORIQ
          </div>
        </div>
        <div className="border-t border-nx-steel/30 px-6 md:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[0.7rem] uppercase tracking-[0.2em] text-nx-steel">
          <div>© 2026 Nexoriq — Global Sourcing &amp; Manufacturing</div>
          <div className="flex items-center gap-6">
            <span className="font-mono">v1.0 · Built for engineers</span>
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="font-mono text-nx-steel/60 hover:text-nx-copper transition-colors"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
